/**
 * CompositeQueryBuilderTab
 *
 * Two-mode composite query builder for the DataDiscoveryModal.
 *
 * AI mode  — type a natural language prompt, press Generate, review the
 *            definition produced by the backend, then Apply it to the chart.
 *
 * Visual mode — three-step wizard:
 *   Step 0 (Sources)  — add 2+ schemas with measures and a time range
 *   Step 1 (Join)     — choose primary source, join type, and join keys
 *   Step 2 (Fields)   — map output aliases and optionally preview before Apply
 */

import React, { useState, useCallback, useEffect, useMemo, useRef } from 'react';
import { Sparkles, Settings2, Plus, Trash2, Loader2, AlertCircle, ChevronRight, ChevronLeft, Eye, Save } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { toast } from 'sonner';
import { dataApi, type DataSource } from '@/services/api/dataApi';
import { executeCompositeQueryDefinition, queryTemplateApi } from '@/services/api/queryTemplateApi';
import type {
  ChartQueryDefinition,
  DataDiscoveryResult,
  CompositeQueryDefinition,
  CompositeQuerySource,
  CompositeFieldMapping,
  CompositeJoinKey,
  TimeGranularity,
  AggregationFunction,
} from '@/types/chartDataSource';
import type { Component } from '@/types';
import { FOREST_GREEN } from '@/styles/tokens';
import { resolveCompositeBindingSelection, type CompositeBindingSelection, type CompositeSourceScopeMetadata } from './compositeScope';
import { useOrganizationStore } from '@/stores/organization/organization-store';
import {
  buildTemplateSaveMetadataFromCompositeDefinition,
  EMPTY_TEMPLATE_SAVE_METADATA,
  type TemplateSaveMetadata,
} from './DataDiscoveryModal.saveMetadata';
import {
  TemplateSaveMetadataFields,
  type TemplateSaveMetadataField,
} from './TemplateSaveMetadataFields';
import {
  getCompositeResultMetadata,
  type CreateTemplateResult,
} from './DataDiscoveryModal.createLogic';
import { getGeneratedCreateActionState } from './DataDiscoveryModal.generatedActions';

// ---------------------------------------------------------------------------
// Local types
// ---------------------------------------------------------------------------

type Mode = 'ai' | 'visual';
type VisualStep = 0 | 1 | 2;
type GeneratedCompositeResult = Extract<CreateTemplateResult, { kind: 'composite' }>;
const MAX_VISUAL_STEP: VisualStep = 2;
const TIME_GRANULARITY_OPTIONS: TimeGranularity[] = ['minute', 'hour', 'day', 'week', 'month'];
const MISSING_JOIN_KEY_REASON = 'Choose at least one join key before saving or applying this manual draft.';
const INVALID_JOIN_KEY_REASON = 'Join keys must include left and right fields.';
const AI_COMPOSITE_ACTION_BLOCKED_REASON =
  'This AI-generated multi-source template needs review and must be rebuilt from trusted source selections before saving or applying.';
const compactSaveInputClassName =
  'w-full rounded-md border border-input bg-background px-2 py-1 text-xs text-foreground ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 disabled:cursor-not-allowed disabled:opacity-50';
const compactSaveTextareaClassName = `${compactSaveInputClassName} min-h-[56px] resize-y leading-4`;

interface AvailableSource {
  id: string;
  name: string;
  groupId: string;
  fields: string[];
  dataOwnerOrganizationId?: string;
  organizationId?: string;
  organizationName?: string;
}

type SourceListItem = DataSource & {
  id?: string;
  groupId?: string;
  dataOwnerOrganizationId?: string;
  organizationId?: string;
  organizationName?: string;
  fields?: string[];
  metadata?: DataSource['metadata'] & {
    groupId?: string;
    dataOwnerOrganizationId?: string;
    organizationName?: string;
  };
};

interface DraftSource {
  /** Unique within this composite, e.g. "pressure", "flow" */
  sourceId: string;
  label: string;
  schemaId: string;
  schemaName: string;
  groupId: string;
  /** Fields available on this schema */
  availableFields: string[];
  measures: Array<{
    field: string;
    aggregation: AggregationFunction;
    alias: string;
  }>;
  timeField: string;
  timeRange?: ChartQueryDefinition['timeRange'];
  timeGranularity: TimeGranularity;
  filters?: ChartQueryDefinition['filters'];
  orderBy?: ChartQueryDefinition['orderBy'];
  limit?: ChartQueryDefinition['limit'];
  aiGeneratedPipeline?: ChartQueryDefinition['aiGeneratedPipeline'];
  dataOwnerOrganizationId?: string;
  organizationId?: string;
  organizationName?: string;
}

interface DraftJoinKey {
  left: string;
  right: string;
  kind: 'exact' | 'time-bucket' | 'nearest-timestamp';
  granularity: TimeGranularity;
  toleranceMs: number;
}

interface SaveMetadataDirtyState {
  name: boolean;
  description: boolean;
  tags: boolean;
  folder: boolean;
}

const CLEAN_SAVE_METADATA_DIRTY_STATE: SaveMetadataDirtyState = {
  name: false,
  description: false,
  tags: false,
  folder: false,
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

export interface CompositeQueryBuilderTabProps {
  component: Component | null | undefined;
  discoveryResult?: DataDiscoveryResult | null;
  onApply: (selection: CompositeBindingSelection) => void;
  hasApplyTarget?: boolean;
  initialDefinition?: CompositeQueryDefinition | null;
  initialSaveMetadata?: TemplateSaveMetadata;
  initialJoinWarning?: string | null;
  initialMode?: Mode;
  showModeToggle?: boolean;
}

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function newDraftSource(
  schemaId: string,
  schemaName: string,
  groupId: string,
  fields: string[],
  sourceScope?: {
    dataOwnerOrganizationId?: string;
    organizationId?: string;
    organizationName?: string;
  },
): DraftSource {
  const id =
    schemaName
      .toLowerCase()
      .replace(/\s+/g, '_')
      .replace(/[^a-z0-9_]/g, '')
      .slice(0, 20) || `src_${Date.now()}`;
  const timeField = fields.find((f) => /time|timestamp|date|ts\b/i.test(f)) || fields[0] || 'timestamp';
  const numericFields = fields.filter((f) => !/time|timestamp|date|ts\b|id\b|name/i.test(f));
  return {
    sourceId: id,
    label: schemaName,
    schemaId,
    schemaName,
    groupId,
    availableFields: fields,
    measures: numericFields.slice(0, 2).map((f) => ({ field: f, aggregation: 'last', alias: f })),
    timeField,
    timeRange: { field: timeField, preset: 'last_24h' },
    timeGranularity: 'hour',
    ...(sourceScope?.dataOwnerOrganizationId
      ? { dataOwnerOrganizationId: sourceScope.dataOwnerOrganizationId }
      : {}),
    ...(sourceScope?.organizationId
      ? { organizationId: sourceScope.organizationId }
      : {}),
    ...(sourceScope?.organizationName
      ? { organizationName: sourceScope.organizationName }
      : {}),
  };
}

function draftToCompositeSource(d: DraftSource): CompositeQuerySource {
  const groupBy = d.timeField
    ? [{ field: d.timeField, granularity: d.timeGranularity }]
    : undefined;

  return {
    id: d.sourceId,
    label: d.label,
    ...(d.dataOwnerOrganizationId
      ? { dataOwnerOrganizationId: d.dataOwnerOrganizationId }
      : {}),
    query: {
      schemaId: d.schemaId,
      groupId: d.groupId,
      schemaName: d.schemaName,
      measures: d.measures.filter((m) => m.field && m.aggregation),
      ...(groupBy ? { groupBy } : {}),
      ...(d.timeRange ? { timeRange: d.timeRange } : {}),
      ...(d.filters ? { filters: d.filters } : {}),
      ...(d.orderBy ? { orderBy: d.orderBy } : {}),
      ...(typeof d.limit === 'number' ? { limit: d.limit } : {}),
      ...(d.aiGeneratedPipeline ? { aiGeneratedPipeline: d.aiGeneratedPipeline } : {}),
      ...(d.organizationId ? { organizationId: d.organizationId } : {}),
      ...(d.organizationName ? { organizationName: d.organizationName } : {}),
    },
  };
}

function autoFields(sources: DraftSource[]): CompositeFieldMapping[] {
  const out: CompositeFieldMapping[] = [];
  const seen = new Set<string>();
  for (const src of sources) {
    // time field from primary
    if (!seen.has(src.timeField)) {
      out.push({ sourceId: src.sourceId, field: src.timeField, alias: 'time' });
      seen.add('time');
    }
    for (const m of src.measures) {
      const alias = m.alias || m.field;
      if (!seen.has(alias)) {
        out.push({ sourceId: src.sourceId, field: m.alias || m.field, alias });
        seen.add(alias);
      }
    }
  }
  return out;
}

function clampVisualStep(step: number): VisualStep {
  if (step <= 0) {
    return 0;
  }

  if (step >= MAX_VISUAL_STEP) {
    return MAX_VISUAL_STEP;
  }

  return 1;
}

function parseDraftJoinKeyKind(value: string): DraftJoinKey['kind'] | null {
  switch (value) {
    case 'exact':
    case 'time-bucket':
    case 'nearest-timestamp':
      return value;
    default:
      return null;
  }
}

function parseTimeGranularity(value: string): TimeGranularity | null {
  switch (value) {
    case 'minute':
    case 'hour':
    case 'day':
    case 'week':
    case 'month':
      return value;
    default:
      return null;
  }
}

function parseAggregationFunction(value: string): AggregationFunction | null {
  switch (value) {
    case 'sum':
    case 'avg':
    case 'min':
    case 'max':
    case 'count':
    case 'last':
    case 'first':
    case 'none':
      return value;
    default:
      return null;
  }
}

interface VisualBuilderState {
  draftSources: DraftSource[];
  primarySourceId: string;
  joinType: CompositeQueryDefinition['joinType'];
  joinKeys: DraftJoinKey[];
  fieldMappings: CompositeFieldMapping[];
  orderByField: string;
  orderByDirection: 'asc' | 'desc';
  limit: number;
  computedFields?: CompositeQueryDefinition['computedFields'];
}

function createEmptyVisualBuilderState(): VisualBuilderState {
  return {
    draftSources: [],
    primarySourceId: '',
    joinType: 'left',
    joinKeys: [
      {
        left: 'time',
        right: 'time',
        kind: 'nearest-timestamp',
        granularity: 'hour',
        toleranceMs: 300000,
      },
    ],
    fieldMappings: [],
    orderByField: 'time',
    orderByDirection: 'asc',
    limit: 1000,
  };
}

function getFirstTimeGroup(source: CompositeQuerySource): { field: string; granularity: TimeGranularity } | null {
  const group = source.query.groupBy?.find((candidate) => candidate.granularity);
  if (!group || !group.granularity) {
    return null;
  }

  return {
    field: group.field,
    granularity: group.granularity,
  };
}

function addField(fields: string[], field: string | undefined): void {
  if (field && !fields.includes(field)) {
    fields.push(field);
  }
}

function getInitialSourceFields(source: CompositeQuerySource, definition: CompositeQueryDefinition): string[] {
  const fields: string[] = [];
  addField(fields, source.query.timeRange?.field);

  for (const group of source.query.groupBy ?? []) {
    addField(fields, group.field);
  }

  for (const measure of source.query.measures) {
    addField(fields, measure.field);
    addField(fields, measure.alias);
  }

  for (const mapping of definition.fields) {
    if (mapping.sourceId === source.id) {
      addField(fields, mapping.field);
    }
  }

  for (const joinKey of definition.joinKeys) {
    addField(fields, joinKey.left);
    addField(fields, joinKey.right);
  }

  return fields;
}

function draftJoinKeyFromCompositeJoinKey(joinKey: CompositeJoinKey): DraftJoinKey {
  if (joinKey.kind === 'nearest-timestamp') {
    return {
      left: joinKey.left,
      right: joinKey.right,
      kind: joinKey.kind,
      granularity: joinKey.granularity ?? 'hour',
      toleranceMs: joinKey.toleranceMs,
    };
  }

  return {
    left: joinKey.left,
    right: joinKey.right,
    kind: joinKey.kind ?? 'exact',
    granularity: joinKey.granularity ?? 'hour',
    toleranceMs: 300000,
  };
}

function draftSourceFromCompositeSource(source: CompositeQuerySource, definition: CompositeQueryDefinition): DraftSource {
  const timeGroup = getFirstTimeGroup(source);
  const timeField = source.query.timeRange?.field ?? timeGroup?.field ?? source.query.groupBy?.[0]?.field ?? getInitialSourceFields(source, definition)[0] ?? 'timestamp';
  const availableFields = getInitialSourceFields(source, definition);
  addField(availableFields, timeField);

  return {
    sourceId: source.id,
    label: source.label ?? source.query.schemaName,
    schemaId: source.query.schemaId,
    schemaName: source.query.schemaName,
    groupId: source.query.groupId,
    availableFields,
    measures: source.query.measures.map((measure) => ({
      field: measure.field,
      aggregation: measure.aggregation,
      alias: measure.alias ?? measure.field,
    })),
    timeField,
    ...(source.query.timeRange ? { timeRange: source.query.timeRange } : {}),
    timeGranularity: timeGroup?.granularity ?? 'hour',
    ...(source.query.filters ? { filters: source.query.filters } : {}),
    ...(source.query.orderBy ? { orderBy: source.query.orderBy } : {}),
    ...(typeof source.query.limit === 'number' ? { limit: source.query.limit } : {}),
    ...(source.query.aiGeneratedPipeline ? { aiGeneratedPipeline: source.query.aiGeneratedPipeline } : {}),
    ...(source.dataOwnerOrganizationId
      ? { dataOwnerOrganizationId: source.dataOwnerOrganizationId }
      : {}),
    ...(source.query.organizationId ? { organizationId: source.query.organizationId } : {}),
    ...(source.query.organizationName ? { organizationName: source.query.organizationName } : {}),
  };
}

function createVisualBuilderState(definition: CompositeQueryDefinition | null | undefined): VisualBuilderState {
  if (!definition) {
    return createEmptyVisualBuilderState();
  }

  const firstOrderBy = definition.orderBy?.[0];

  return {
    draftSources: definition.sources.map((source) => draftSourceFromCompositeSource(source, definition)),
    primarySourceId: definition.primarySourceId,
    joinType: definition.joinType,
    joinKeys: definition.joinKeys.map(draftJoinKeyFromCompositeJoinKey),
    fieldMappings: definition.fields,
    orderByField: firstOrderBy?.field ?? 'time',
    orderByDirection: firstOrderBy?.direction ?? 'asc',
    limit: definition.limit ?? 1000,
    ...(definition.computedFields ? { computedFields: definition.computedFields } : {}),
  };
}

function getGeneratedCompositeActionBlockedReason(
  result: GeneratedCompositeResult,
  action: 'save' | 'apply',
): string {
  const actionState = getGeneratedCreateActionState(result);
  return (
    actionState[action] ??
    result.metadata.saveDisabledReason ??
    result.metadata.reviewReason ??
    AI_COMPOSITE_ACTION_BLOCKED_REASON
  );
}

function validateCompositeJoinKeys(definition: CompositeQueryDefinition): string | null {
  if (definition.joinKeys.length === 0) {
    return MISSING_JOIN_KEY_REASON;
  }

  for (const joinKey of definition.joinKeys) {
    if (!joinKey.left.trim() || !joinKey.right.trim()) {
      return INVALID_JOIN_KEY_REASON;
    }

    if (joinKey.kind === 'nearest-timestamp' && (!Number.isFinite(joinKey.toleranceMs) || joinKey.toleranceMs <= 0)) {
      return MISSING_JOIN_KEY_REASON;
    }
  }

  return null;
}

function getInitialSaveMetadata(
  definition: CompositeQueryDefinition | null | undefined,
  metadata: TemplateSaveMetadata | undefined,
): TemplateSaveMetadata {
  if (!definition) {
    return {
      name: metadata?.name ?? EMPTY_TEMPLATE_SAVE_METADATA.name,
      description:
        metadata?.description ?? EMPTY_TEMPLATE_SAVE_METADATA.description,
      tags: metadata?.tags ?? EMPTY_TEMPLATE_SAVE_METADATA.tags,
      folder: metadata?.folder ?? EMPTY_TEMPLATE_SAVE_METADATA.folder,
    };
  }

  const defaults = buildTemplateSaveMetadataFromCompositeDefinition(definition);

  return {
    name: metadata?.name ?? defaults.name,
    description: metadata?.description ?? defaults.description,
    tags: metadata?.tags ?? defaults.tags,
    folder: metadata?.folder ?? defaults.folder,
  };
}

function getImportedSaveMetadataSourceKey(
  definition: CompositeQueryDefinition | null | undefined,
  metadata: TemplateSaveMetadata | undefined,
): string {
  return stableStringify({
    source: 'import',
    definition,
    metadata: getInitialSaveMetadata(definition, metadata),
  });
}

function getGeneratedSaveMetadataSourceKey(
  definition: CompositeQueryDefinition,
  prompt: string,
  explanation: string,
): string {
  return stableStringify({
    source: 'generated',
    definition,
    prompt,
    explanation,
  });
}

function stableStringify(value: unknown): string {
  return JSON.stringify(sortValue(value));
}

function sortValue(value: unknown): unknown {
  if (Array.isArray(value)) {
    return value.map(sortValue);
  }

  if (!isRecord(value)) {
    return value;
  }

  return Object.keys(value)
    .sort()
    .reduce<Record<string, unknown>>((acc, key) => {
      acc[key] = sortValue(value[key]);
      return acc;
    }, {});
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function toAvailableSource(source: SourceListItem): AvailableSource {
  const schemaFields = source.schema?.tables.flatMap((table) => table.fields.map((field) => field.name)) ?? [];

  return {
    id: source.id ?? source.dataSourceId,
    name: source.name,
    groupId: source.groupId ?? source.metadata?.groupId ?? '',
    fields: source.fields ?? schemaFields,
    dataOwnerOrganizationId:
      source.dataOwnerOrganizationId ??
      source.metadata?.dataOwnerOrganizationId ??
      source.organizationId,
    organizationId: source.organizationId,
    organizationName: source.organizationName ?? source.metadata?.organizationName,
  };
}

function getAvailableSourcesFromDiscoveryResult(discoveryResult: DataDiscoveryResult | null | undefined): AvailableSource[] {
  if (!discoveryResult) {
    return [];
  }

  return discoveryResult.categories.flatMap((category) =>
    category.schemas.flatMap((schema) =>
      schema.groupId
        ? [
            {
              id: schema.id,
              name: schema.displayName,
              groupId: schema.groupId,
              fields: schema.fields ?? [],
              dataOwnerOrganizationId: schema.organizationId,
              organizationId: schema.organizationId,
              organizationName: schema.organizationName,
            },
          ]
        : [],
    ),
  );
}

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export function CompositeQueryBuilderTab({
  component: _component,
  discoveryResult,
  onApply,
  hasApplyTarget = true,
  initialDefinition,
  initialSaveMetadata,
  initialJoinWarning,
  initialMode = 'ai',
  showModeToggle = true,
}: CompositeQueryBuilderTabProps) {
  const initialVisualState = useMemo(() => createVisualBuilderState(initialDefinition), [initialDefinition]);
  const initialSaveState = useMemo(
    () => getInitialSaveMetadata(initialDefinition, initialSaveMetadata),
    [initialDefinition, initialSaveMetadata],
  );
  const importedSaveMetadataSourceKey = useMemo(
    () => getImportedSaveMetadataSourceKey(initialDefinition, initialSaveMetadata),
    [initialDefinition, initialSaveMetadata],
  );
  const saveMetadataSourceKeyRef = useRef(importedSaveMetadataSourceKey);

  // ── Mode toggle ──────────────────────────────────────────────────────────
  const [mode, setMode] = useState<Mode>(initialMode);

  // ── AI mode state ────────────────────────────────────────────────────────
  const [aiPrompt, setAiPrompt] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [aiError, setAiError] = useState<string | null>(null);
  const [aiResult, setAiResult] = useState<GeneratedCompositeResult | null>(null);

  // ── Visual mode state ────────────────────────────────────────────────────
  const [step, setStep] = useState<VisualStep>(0);
  const [availableSources, setAvailableSources] = useState<AvailableSource[]>([]);
  const [sourcesLoaded, setSourcesLoaded] = useState(false);
  const [sourcesLoading, setSourcesLoading] = useState(false);
  const [draftSources, setDraftSources] = useState<DraftSource[]>(initialVisualState.draftSources);

  const [primarySourceId, setPrimarySourceId] = useState(initialVisualState.primarySourceId);
  const [joinType, setJoinType] = useState<CompositeQueryDefinition['joinType']>(initialVisualState.joinType);
  const [joinKeys, setJoinKeys] = useState<DraftJoinKey[]>(initialVisualState.joinKeys);

  const [fieldMappings, setFieldMappings] = useState<CompositeFieldMapping[]>(initialVisualState.fieldMappings);
  const [orderByField, setOrderByField] = useState(initialVisualState.orderByField);
  const [orderByDirection, setOrderByDirection] = useState<'asc' | 'desc'>(initialVisualState.orderByDirection);
  const [limit, setLimit] = useState(initialVisualState.limit);
  const [computedFields, setComputedFields] = useState<CompositeQueryDefinition['computedFields']>(initialVisualState.computedFields);

  // ── Preview state ─────────────────────────────────────────────────────────
  const [previewLoading, setPreviewLoading] = useState(false);
  const [previewError, setPreviewError] = useState<string | null>(null);
  const [previewRows, setPreviewRows] = useState<Record<string, unknown>[] | null>(null);
  const [joinValidationMessage, setJoinValidationMessage] = useState<string | null>(initialJoinWarning ?? null);
  const [saveLoading, setSaveLoading] = useState(false);
  const [saveName, setSaveName] = useState(initialSaveState.name);
  const [saveDescription, setSaveDescription] = useState(initialSaveState.description);
  const [saveTags, setSaveTags] = useState(initialSaveState.tags);
  const [saveFolder, setSaveFolder] = useState(initialSaveState.folder);
  const [saveMetadataDirty, setSaveMetadataDirty] = useState<SaveMetadataDirtyState>(CLEAN_SAVE_METADATA_DIRTY_STATE);
  const discoveryAvailableSources = useMemo(() => getAvailableSourcesFromDiscoveryResult(discoveryResult), [discoveryResult]);
  const compositeSourceMetadata = useMemo<CompositeSourceScopeMetadata[]>(
    () =>
      (availableSources.length > 0 ? availableSources : discoveryAvailableSources).map((source) => ({
        schemaId: source.id,
        dataOwnerOrganizationId: source.dataOwnerOrganizationId,
        organizationId: source.organizationId,
        organizationName: source.organizationName,
      })),
    [availableSources, discoveryAvailableSources],
  );

  // ── Visual mode helpers ──────────────────────────────────────────────────

  const loadSources = useCallback(async () => {
    if (sourcesLoaded) return;
    if (discoveryAvailableSources.length > 0) {
      setAvailableSources(discoveryAvailableSources);
      setSourcesLoaded(true);
      return;
    }
    setSourcesLoading(true);
    try {
      const result = await dataApi.getSources();
      setAvailableSources(result.map(toAvailableSource));
      setSourcesLoaded(true);
    } catch {
      // ignore — user can still type IDs manually
    } finally {
      setSourcesLoading(false);
    }
  }, [discoveryAvailableSources, sourcesLoaded]);

  useEffect(() => {
    if (mode === 'visual') {
      loadSources();
    }
  }, [loadSources, mode]);

  useEffect(() => {
    setDraftSources(initialVisualState.draftSources);
    setPrimarySourceId(initialVisualState.primarySourceId);
    setJoinType(initialVisualState.joinType);
    setJoinKeys(initialVisualState.joinKeys);
    setFieldMappings(initialVisualState.fieldMappings);
    setOrderByField(initialVisualState.orderByField);
    setOrderByDirection(initialVisualState.orderByDirection);
    setLimit(initialVisualState.limit);
    setComputedFields(initialVisualState.computedFields);
    setJoinValidationMessage(initialJoinWarning ?? null);
    setPreviewError(null);
    setPreviewRows(null);
  }, [initialJoinWarning, initialVisualState]);

  useEffect(() => {
    if (saveMetadataSourceKeyRef.current === importedSaveMetadataSourceKey) {
      return;
    }

    saveMetadataSourceKeyRef.current = importedSaveMetadataSourceKey;
    setSaveName(initialSaveState.name);
    setSaveDescription(initialSaveState.description);
    setSaveTags(initialSaveState.tags);
    setSaveFolder(initialSaveState.folder);
    setSaveMetadataDirty(CLEAN_SAVE_METADATA_DIRTY_STATE);
  }, [importedSaveMetadataSourceKey, initialSaveState]);

  const markSaveMetadataDirty = useCallback((field: keyof SaveMetadataDirtyState) => {
    setSaveMetadataDirty((prev) => (prev[field] ? prev : { ...prev, [field]: true }));
  }, []);

  const handleSaveNameChange = useCallback(
    (value: string) => {
      markSaveMetadataDirty('name');
      setSaveName(value);
    },
    [markSaveMetadataDirty],
  );

  const handleSaveDescriptionChange = useCallback(
    (value: string) => {
      markSaveMetadataDirty('description');
      setSaveDescription(value);
    },
    [markSaveMetadataDirty],
  );

  const handleSaveTagsChange = useCallback(
    (value: string) => {
      markSaveMetadataDirty('tags');
      setSaveTags(value);
    },
    [markSaveMetadataDirty],
  );

  const handleSaveFolderChange = useCallback(
    (value: string) => {
      markSaveMetadataDirty('folder');
      setSaveFolder(value);
    },
    [markSaveMetadataDirty],
  );

  const resolveBinding = useCallback(
    (definition: CompositeQueryDefinition) =>
      resolveCompositeBindingSelection(definition, compositeSourceMetadata, {
        isPersonalSpace: useOrganizationStore.getState().currentContext?.type === 'user',
      }),
    [compositeSourceMetadata],
  );

  const handleSwitchToVisual = useCallback(() => {
    setMode('visual');
    loadSources();
  }, [loadSources]);

  const handleSwitchToAI = useCallback(() => {
    setMode('ai');
  }, []);

  const addSource = useCallback((schema: AvailableSource) => {
    setDraftSources((prev) => {
      if (prev.some((s) => s.schemaId === schema.id)) return prev;
      const draft = newDraftSource(schema.id, schema.name, schema.groupId, schema.fields, {
        dataOwnerOrganizationId: schema.dataOwnerOrganizationId,
        organizationId: schema.organizationId,
        organizationName: schema.organizationName,
      });
      const next = [...prev, draft];
      if (next.length === 1) setPrimarySourceId(draft.sourceId);
      return next;
    });
  }, []);

  const removeSource = useCallback(
    (idx: number) => {
      setDraftSources((prev) => {
        const next = prev.filter((_, i) => i !== idx);
        if (next.length > 0 && !next.find((s) => s.sourceId === primarySourceId)) {
          setPrimarySourceId(next[0].sourceId);
        }
        return next;
      });
    },
    [primarySourceId],
  );

  const buildDefinition = useCallback((): CompositeQueryDefinition => {
    return {
      sources: draftSources.map(draftToCompositeSource),
      primarySourceId,
      joinType,
      joinKeys: joinKeys.map((k) =>
        k.kind === 'nearest-timestamp'
          ? {
              left: k.left,
              right: k.right,
              kind: k.kind,
              toleranceMs: k.toleranceMs,
              granularity: k.granularity,
            }
          : k.kind === 'time-bucket'
            ? {
                left: k.left,
                right: k.right,
                kind: k.kind,
                granularity: k.granularity,
              }
            : { left: k.left, right: k.right, kind: k.kind },
      ),
      fields: fieldMappings,
      ...(computedFields ? { computedFields } : {}),
      orderBy: orderByField ? [{ field: orderByField, direction: orderByDirection }] : undefined,
      limit,
    };
  }, [draftSources, primarySourceId, joinType, joinKeys, fieldMappings, computedFields, orderByField, orderByDirection, limit]);

  useEffect(() => {
    if (joinValidationMessage && validateCompositeJoinKeys(buildDefinition()) === null) {
      setJoinValidationMessage(null);
    }
  }, [buildDefinition, joinValidationMessage]);

  const handlePreview = useCallback(
    async (def: CompositeQueryDefinition) => {
      const joinValidationReason = validateCompositeJoinKeys(def);
      if (joinValidationReason) {
        setJoinValidationMessage(joinValidationReason);
        setStep(1);
        toast.error(joinValidationReason);
        return;
      }

      setPreviewLoading(true);
      setPreviewError(null);
      setPreviewRows(null);
      try {
        const resolvedBinding = resolveBinding(def);
        if (resolvedBinding.error) {
          setPreviewError(resolvedBinding.error);
          return;
        }

        const resp = await executeCompositeQueryDefinition(
          { ...resolvedBinding.definition, limit: 5 },
          resolvedBinding.organizationId ? { organizationId: resolvedBinding.organizationId } : undefined,
        );
        if (!resp.success) {
          setPreviewError(resp.error || 'Preview failed');
        } else {
          setPreviewRows((resp.data || []).slice(0, 5));
        }
      } catch (err) {
        setPreviewError(err instanceof Error ? err.message : 'Preview failed');
      } finally {
        setPreviewLoading(false);
      }
    },
    [resolveBinding],
  );

  const handleVisualApply = useCallback(() => {
    const definition = buildDefinition();
    const joinValidationReason = validateCompositeJoinKeys(definition);
    if (joinValidationReason) {
      setJoinValidationMessage(joinValidationReason);
      setStep(1);
      toast.error(joinValidationReason);
      return;
    }

    const resolvedBinding = resolveBinding(definition);
    if (resolvedBinding.error) {
      setPreviewError(resolvedBinding.error);
      toast.error(resolvedBinding.error);
      return;
    }

    setPreviewError(null);
    onApply(resolvedBinding);
  }, [buildDefinition, onApply, resolveBinding]);

  // ── AI mode handlers ─────────────────────────────────────────────────────

  const handleGenerate = useCallback(async () => {
    if (!aiPrompt.trim() || aiLoading) return;
    setAiLoading(true);
    setAiError(null);
    setAiResult(null);
    try {
      const result = await dataApi.generateCompositeQuery(aiPrompt.trim());
      const generatedDefinition = result.compositeQueryDefinition;
      if (!generatedDefinition) {
        throw new Error('Generation did not return a composite query definition');
      }

      const generatedPrompt = aiPrompt.trim();
      const generatedCompositeResult: GeneratedCompositeResult = {
        kind: 'composite',
        definition: generatedDefinition,
        explanation: result.explanation,
        previewRows: [],
        metadata: getCompositeResultMetadata(
          generatedDefinition,
          generatedPrompt,
          true,
          result.reviewReason,
        ),
      };
      setAiResult(generatedCompositeResult);
      const generatedMetadata = buildTemplateSaveMetadataFromCompositeDefinition(
        generatedDefinition,
        generatedPrompt,
        result.explanation,
      );
      const generatedMetadataSourceKey = getGeneratedSaveMetadataSourceKey(
        generatedDefinition,
        generatedPrompt,
        result.explanation,
      );
      const isNewGeneratedResult = saveMetadataSourceKeyRef.current !== generatedMetadataSourceKey;
      saveMetadataSourceKeyRef.current = generatedMetadataSourceKey;
      setSaveName((current) => (isNewGeneratedResult || !saveMetadataDirty.name ? generatedMetadata.name : current));
      setSaveDescription((current) => (isNewGeneratedResult || !saveMetadataDirty.description ? generatedMetadata.description : current));
      setSaveTags((current) => (isNewGeneratedResult || !saveMetadataDirty.tags ? generatedMetadata.tags : current));
      setSaveFolder((current) => (isNewGeneratedResult || !saveMetadataDirty.folder ? generatedMetadata.folder : current));
      if (isNewGeneratedResult) {
        setSaveMetadataDirty(CLEAN_SAVE_METADATA_DIRTY_STATE);
      }
    } catch (err) {
      setAiError(err instanceof Error ? err.message : 'Generation failed');
    } finally {
      setAiLoading(false);
    }
  }, [aiPrompt, aiLoading, saveMetadataDirty]);

  const handleAIApply = useCallback(() => {
    if (!aiResult) {
      return;
    }

    const actionState = getGeneratedCreateActionState(aiResult);
    if (!actionState.canApply) {
      const blockedReason = getGeneratedCompositeActionBlockedReason(aiResult, 'apply');
      setAiError(blockedReason);
      toast.error(blockedReason);
      return;
    }

    const joinValidationReason = validateCompositeJoinKeys(aiResult.definition);
    if (joinValidationReason) {
      setAiError(joinValidationReason);
      toast.error(joinValidationReason);
      return;
    }

    const resolvedBinding = resolveBinding(aiResult.definition);
    if (resolvedBinding.error) {
      setAiError(resolvedBinding.error);
      toast.error(resolvedBinding.error);
      return;
    }

    setAiError(null);
    onApply(resolvedBinding);
  }, [aiResult, onApply, resolveBinding]);

  const handleSave = useCallback(
    async (
      definition: CompositeQueryDefinition,
      options: { generatedResult?: GeneratedCompositeResult } = {},
    ) => {
      if (!saveName.trim() || saveLoading) return;

      if (options.generatedResult) {
        const actionState = getGeneratedCreateActionState(options.generatedResult);
        if (!actionState.canSave) {
          const blockedReason = getGeneratedCompositeActionBlockedReason(options.generatedResult, 'save');
          setAiError(blockedReason);
          toast.error(blockedReason);
          return;
        }
      }

      const joinValidationReason = validateCompositeJoinKeys(definition);
      if (joinValidationReason) {
        setJoinValidationMessage(joinValidationReason);
        if (mode === 'visual') {
          setStep(1);
        } else {
          setAiError(joinValidationReason);
        }
        toast.error(joinValidationReason);
        return;
      }

      const resolvedBinding = resolveBinding(definition);
      if (resolvedBinding.error) {
        toast.error(resolvedBinding.error);
        return;
      }

      setSaveLoading(true);
      try {
        await queryTemplateApi.create(
          {
            name: saveName.trim(),
            description: saveDescription.trim() || undefined,
            templateType: 'composite-query',
            compositeQueryDefinition: resolvedBinding.definition,
            tags: saveTags
              .split(',')
              .map((tag) => tag.trim())
              .filter(Boolean),
            folder: saveFolder.trim() || undefined,
          },
          resolvedBinding.organizationId ? { organizationId: resolvedBinding.organizationId } : undefined,
        );
        toast.success('Composite template saved');
        setSaveName('');
        setSaveDescription('');
        setSaveTags('');
        setSaveFolder('');
      } catch (err) {
        toast.error(err instanceof Error ? err.message : 'Failed to save composite template');
      } finally {
        setSaveLoading(false);
      }
    },
    [mode, resolveBinding, saveDescription, saveFolder, saveLoading, saveName, saveTags],
  );

  const aiActionState = useMemo(
    () => (aiResult ? getGeneratedCreateActionState(aiResult) : null),
    [aiResult],
  );
  const aiReviewReason = aiResult?.metadata.needsReview
    ? aiActionState?.save ?? aiActionState?.apply ?? aiResult.metadata.reviewReason ?? AI_COMPOSITE_ACTION_BLOCKED_REASON
    : null;

  // ── Rendering ─────────────────────────────────────────────────────────────

  return (
    <div className="flex h-full min-h-0 flex-col">
      {/* Mode toggle */}
      {showModeToggle && (
        <>
          <div className="flex shrink-0 gap-1 p-4 pb-0">
            <Button
              variant={mode === 'ai' ? 'default' : 'outline'}
              size="sm"
              onClick={handleSwitchToAI}
              className="flex items-center gap-1.5 text-xs"
              style={mode === 'ai' ? { backgroundColor: FOREST_GREEN.DEFAULT, color: 'white' } : {}}
            >
              <Sparkles className="h-3.5 w-3.5" />
              AI Generate
            </Button>
            <Button
              variant={mode === 'visual' ? 'default' : 'outline'}
              size="sm"
              onClick={handleSwitchToVisual}
              className="flex items-center gap-1.5 text-xs"
              style={mode === 'visual' ? { backgroundColor: FOREST_GREEN.DEFAULT, color: 'white' } : {}}
            >
              <Settings2 className="h-3.5 w-3.5" />
              Build Manually
            </Button>
          </div>

          <Separator className="mt-3" />
        </>
      )}

      {/* ── AI mode ── */}
      {mode === 'ai' && (
        <div className="min-h-0 flex-1 overflow-y-auto p-4 space-y-4">
          <div className="space-y-1.5">
            <Label className="text-xs font-medium text-gray-700 dark:text-gray-300">What data do you want to build?</Label>
            <Textarea
              placeholder='e.g. "compare pressure and flow for well 123 over the last 24 hours"'
              value={aiPrompt}
              onChange={(e) => setAiPrompt(e.target.value)}
              className="text-sm min-h-[72px] resize-none"
              onKeyDown={(e) => {
                if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) handleGenerate();
              }}
            />
            <p className="text-[11px] text-gray-400 dark:text-gray-500">Cmd/Ctrl + Enter to generate</p>
          </div>

          <Button
            size="sm"
            disabled={!aiPrompt.trim() || aiLoading}
            onClick={handleGenerate}
            className="w-full"
            style={{ backgroundColor: FOREST_GREEN.DEFAULT, color: 'white' }}
          >
            {aiLoading ? (
              <>
                <Loader2 className="h-3.5 w-3.5 animate-spin mr-1.5" /> Generating…
              </>
            ) : (
              'Generate →'
            )}
          </Button>

          {aiError && (
            <div className="flex items-start gap-2 text-red-600 text-xs bg-red-50 dark:bg-red-900/20 rounded-md px-3 py-2">
              <AlertCircle className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
              <span>{aiError}</span>
            </div>
          )}

          {aiResult && (
            <div className="space-y-3">
              <div className="border border-emerald-200 dark:border-emerald-800 bg-emerald-50 dark:bg-emerald-900/20 rounded-lg p-3 space-y-2">
                <p className="text-xs font-medium text-emerald-800 dark:text-emerald-300">Generated definition</p>
                <div className="space-y-1">
                  {aiResult.definition.sources.map((src) => (
                    <div key={src.id} className="flex items-center gap-1.5">
                      <Badge variant="secondary" className="text-[10px] px-1.5 py-0">
                        {src.id}
                      </Badge>
                      <span className="text-xs text-gray-600 dark:text-gray-400">{src.label || src.query.schemaName}</span>
                    </div>
                  ))}
                </div>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Join:</span> {aiResult.definition.joinType} • {aiResult.definition.joinKeys.length} key
                  {aiResult.definition.joinKeys.length !== 1 ? 's' : ''}
                </p>
                <p className="text-xs text-gray-600 dark:text-gray-400">
                  <span className="font-medium">Output fields:</span> {aiResult.definition.fields.map((f) => f.alias).join(', ')}
                </p>
                {aiResult.explanation && (
                  <p className="text-[11px] text-gray-500 dark:text-gray-400 italic border-t border-emerald-200 dark:border-emerald-800 pt-2">
                    {aiResult.explanation}
                  </p>
                )}
                {aiReviewReason && (
                  <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-2 py-1.5 text-xs text-amber-800 dark:border-amber-900 dark:bg-amber-900/20 dark:text-amber-300">
                    <AlertCircle className="mt-0.5 h-3.5 w-3.5 flex-shrink-0" />
                    <span>{aiReviewReason}</span>
                  </div>
                )}
              </div>

              {/* Action buttons */}
              <div className="flex gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  className="flex items-center gap-1.5 text-xs"
                  disabled={previewLoading}
                  onClick={() => handlePreview(aiResult.definition)}
                >
                  {previewLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3 w-3" />}
                  Preview
                </Button>
                <Button
                  size="sm"
                  variant={hasApplyTarget ? 'outline' : 'default'}
                  className={hasApplyTarget ? 'text-xs' : 'flex-1 text-xs'}
                  disabled={!saveName.trim() || saveLoading || aiActionState?.canSave === false}
                  style={!hasApplyTarget ? { backgroundColor: FOREST_GREEN.DEFAULT, color: 'white' } : undefined}
                  onClick={() => handleSave(aiResult.definition, { generatedResult: aiResult })}
                >
                  {saveLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Save className="h-3 w-3" />}
                  Save
                </Button>
                {hasApplyTarget && (
                  <Button
                    size="sm"
                    className="flex-1 text-xs"
                    style={{
                      backgroundColor: FOREST_GREEN.DEFAULT,
                      color: 'white',
                    }}
                    disabled={aiActionState?.canApply === false}
                    onClick={handleAIApply}
                  >
                    Apply to Chart
                  </Button>
                )}
              </div>

              {/* Preview results — shown immediately after buttons */}
              {previewError && <p className="text-xs text-red-600 bg-red-50 dark:bg-red-900/20 rounded px-2 py-1">{previewError}</p>}
              {previewRows && previewRows.length > 0 && <PreviewTable rows={previewRows} />}
              {previewRows && previewRows.length === 0 && !previewError && (
                <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded px-2 py-1">
                  No data returned. The matched schemas may not contain data for the requested time range, or the schema names may not match what you expected.
                  Try using the &ldquo;Build Manually&rdquo; tab to pick exact schemas.
                </p>
              )}

              <SaveTemplateFields
                name={saveName}
                description={saveDescription}
                tags={saveTags}
                folder={saveFolder}
                onNameChange={handleSaveNameChange}
                onDescriptionChange={handleSaveDescriptionChange}
                onTagsChange={handleSaveTagsChange}
                onFolderChange={handleSaveFolderChange}
              />
            </div>
          )}
        </div>
      )}

      {/* ── Visual mode ── */}
      {mode === 'visual' && (
        <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
          {/* Step indicator */}
          <div className="flex shrink-0 items-center gap-1 px-4 py-2 text-xs text-gray-500 dark:text-gray-400">
            {(['Sources', 'Join', 'Fields'] as const).map((label, i) => (
              <React.Fragment key={label}>
                <span
                  className={`font-medium ${step === i ? 'text-emerald-700 dark:text-emerald-400' : step > i ? 'text-gray-400 dark:text-gray-500 line-through' : ''}`}
                >
                  {i + 1}. {label}
                </span>
                {i < 2 && <ChevronRight className="h-3 w-3 text-gray-300 dark:text-gray-600" />}
              </React.Fragment>
            ))}
          </div>
          <Separator />

          <div className="min-h-0 flex-1 overflow-y-auto p-4 pb-8 space-y-4">
            {/* ─── Step 0: Sources ─── */}
            {step === 0 && (
              <VisualStep0
                availableSources={availableSources}
                sourcesLoading={sourcesLoading}
                draftSources={draftSources}
                onAdd={addSource}
                onRemove={removeSource}
                onChangeDraft={(idx, patch) => setDraftSources((prev) => prev.map((s, i) => (i === idx ? { ...s, ...patch } : s)))}
              />
            )}

            {/* ─── Step 1: Join ─── */}
            {step === 1 && (
              <VisualStep1
                draftSources={draftSources}
                primarySourceId={primarySourceId}
                joinType={joinType}
                joinKeys={joinKeys}
                joinValidationMessage={joinValidationMessage}
                onSetPrimary={setPrimarySourceId}
                onSetJoinType={setJoinType}
                onSetJoinKeys={setJoinKeys}
              />
            )}

            {/* ─── Step 2: Fields ─── */}
            {step === 2 && (
              <VisualStep2
                draftSources={draftSources}
                fieldMappings={fieldMappings}
                orderByField={orderByField}
                orderByDirection={orderByDirection}
                limit={limit}
                saveName={saveName}
                saveDescription={saveDescription}
                saveTags={saveTags}
                saveFolder={saveFolder}
                previewLoading={previewLoading}
                previewError={previewError}
                previewRows={previewRows}
                onSetMappings={setFieldMappings}
                onSetOrderBy={setOrderByField}
                onSetOrderByDirection={setOrderByDirection}
                onSetLimit={setLimit}
                onSaveNameChange={handleSaveNameChange}
                onSaveDescriptionChange={handleSaveDescriptionChange}
                onSaveTagsChange={handleSaveTagsChange}
                onSaveFolderChange={handleSaveFolderChange}
                onAutoPopulate={() => setFieldMappings(autoFields(draftSources))}
                onPreview={() => handlePreview(buildDefinition())}
              />
            )}
          </div>

          {/* Navigation */}
          <Separator className="shrink-0" />
          <div className="flex shrink-0 justify-between items-center px-4 py-3 bg-background">
            <Button size="sm" variant="ghost" className="text-xs" disabled={step === 0} onClick={() => setStep((s) => clampVisualStep(s - 1))}>
              <ChevronLeft className="h-3.5 w-3.5 mr-1" /> Back
            </Button>

            {step < 2 ? (
              <Button
                size="sm"
                disabled={step === 0 && draftSources.length < 2}
                onClick={() => {
                  if (step === 1 && fieldMappings.length === 0) {
                    setFieldMappings(autoFields(draftSources));
                  }
                  setStep((s) => clampVisualStep(s + 1));
                }}
                style={{
                  backgroundColor: FOREST_GREEN.DEFAULT,
                  color: 'white',
                }}
              >
                Next <ChevronRight className="h-3.5 w-3.5 ml-1" />
              </Button>
            ) : (
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant={hasApplyTarget ? 'outline' : 'default'}
                  disabled={fieldMappings.length === 0 || !saveName.trim() || saveLoading}
                  style={!hasApplyTarget ? { backgroundColor: FOREST_GREEN.DEFAULT, color: 'white' } : undefined}
                  onClick={() => handleSave(buildDefinition())}
                >
                  {saveLoading ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Save className="h-3.5 w-3.5 mr-1" />}
                  Save
                </Button>
                {hasApplyTarget && (
                  <Button
                    size="sm"
                    disabled={fieldMappings.length === 0}
                    style={{
                      backgroundColor: FOREST_GREEN.DEFAULT,
                      color: 'white',
                    }}
                    onClick={handleVisualApply}
                  >
                    Apply to Chart
                  </Button>
                )}
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Visual step sub-components
// ---------------------------------------------------------------------------

function VisualStep0({
  availableSources,
  sourcesLoading,
  draftSources,
  onAdd,
  onRemove,
  onChangeDraft,
}: {
  availableSources: AvailableSource[];
  sourcesLoading: boolean;
  draftSources: DraftSource[];
  onAdd: (s: AvailableSource) => void;
  onRemove: (i: number) => void;
  onChangeDraft: (i: number, patch: Partial<DraftSource>) => void;
}) {
  const aggregations = ['last', 'avg', 'sum', 'min', 'max', 'count', 'first', 'none'];
  const timePresets = [
    { value: 'last_hour', label: 'Last hour' },
    { value: 'last_24h', label: 'Last 24 h' },
    { value: 'last_7d', label: 'Last 7 days' },
    { value: 'last_30d', label: 'Last 30 days' },
    { value: 'today', label: 'Today' },
    { value: 'yesterday', label: 'Yesterday' },
    { value: 'previous_24h', label: 'Previous 24 h' },
    { value: 'custom', label: 'Custom range' },
  ];

  return (
    <div className="space-y-4">
      {draftSources.length < 2 ? (
        <p className="text-xs text-gray-500 dark:text-gray-400">Add at least 2 schemas for a multi-source template.</p>
      ) : (
        <p className="text-xs text-emerald-700 dark:text-emerald-300">
          {draftSources.length} schemas selected. Review source fields, time ranges, and measures before continuing.
        </p>
      )}

      {/* Added sources */}
      {draftSources.map((draft, idx) => (
        <div key={draft.sourceId} className="border rounded-lg p-3 space-y-3 bg-gray-50 dark:bg-gray-800/50">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              <Badge variant="secondary" className="text-[10px]">
                {draft.sourceId}
              </Badge>
              <span className="text-xs font-medium text-gray-700 dark:text-gray-300">{draft.label}</span>
            </div>
            <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => onRemove(idx)}>
              <Trash2 className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
            </Button>
          </div>

          {/* Time configuration */}
          <div className="grid grid-cols-3 gap-2">
            <div className="space-y-1">
              <Label className="text-[11px] text-gray-500 dark:text-gray-400">Time field</Label>
              <Select
                value={draft.timeField}
                onValueChange={(value) =>
                  onChangeDraft(idx, {
                    timeField: value,
                    timeRange: draft.timeRange ? { ...draft.timeRange, field: value } : { field: value, preset: 'last_24h' },
                  })
                }
              >
                <SelectTrigger className="h-7 text-xs flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {draft.availableFields.map((field) => (
                    <SelectItem key={field} value={field} className="text-xs">
                      {field}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] text-gray-500 dark:text-gray-400">Time range</Label>
              <Select
                value={draft.timeRange?.preset ?? 'custom'}
                onValueChange={(value) => {
                  if (value === 'custom') {
                    return;
                  }

                  onChangeDraft(idx, {
                    timeRange: { field: draft.timeField, preset: value },
                  });
                }}
              >
                <SelectTrigger className="h-7 text-xs flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {timePresets.map((preset) => (
                    <SelectItem key={preset.value} value={preset.value} className="text-xs">
                      {preset.label}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <Label className="text-[11px] text-gray-500 dark:text-gray-400">Group by</Label>
              <Select
                value={draft.timeGranularity}
                onValueChange={(value) => {
                  const granularity = parseTimeGranularity(value);
                  if (granularity) {
                    onChangeDraft(idx, { timeGranularity: granularity });
                  }
                }}
              >
                <SelectTrigger className="h-7 text-xs flex-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {TIME_GRANULARITY_OPTIONS.map((granularity) => (
                    <SelectItem key={granularity} value={granularity} className="text-xs">
                      {granularity}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Measures */}
          <div className="space-y-1.5">
            <Label className="text-[11px] text-gray-500 dark:text-gray-400">Measures</Label>
            {draft.measures.map((m, mi) => (
              <div key={mi} className="flex gap-1 items-center">
                <Select
                  value={m.field}
                  onValueChange={(v) =>
                    onChangeDraft(idx, {
                      measures: draft.measures.map((mm, j) => (j === mi ? { ...mm, field: v, alias: v } : mm)),
                    })
                  }
                >
                  <SelectTrigger className="h-7 text-xs flex-1">
                    <SelectValue placeholder="field" />
                  </SelectTrigger>
                  <SelectContent>
                    {draft.availableFields.map((f) => (
                      <SelectItem key={f} value={f} className="text-xs">
                        {f}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select
                  value={m.aggregation}
                  onValueChange={(v) => {
                    const aggregation = parseAggregationFunction(v);
                    if (!aggregation) {
                      return;
                    }

                    onChangeDraft(idx, {
                      measures: draft.measures.map((mm, j) => (j === mi ? { ...mm, aggregation } : mm)),
                    });
                  }}
                >
                  <SelectTrigger className="h-7 text-xs w-20">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {aggregations.map((a) => (
                      <SelectItem key={a} value={a} className="text-xs">
                        {a}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Button
                  variant="ghost"
                  size="sm"
                  className="h-7 w-7 p-0 shrink-0"
                  onClick={() =>
                    onChangeDraft(idx, {
                      measures: draft.measures.filter((_, j) => j !== mi),
                    })
                  }
                >
                  <Trash2 className="h-3 w-3 text-gray-400 dark:text-gray-500" />
                </Button>
              </div>
            ))}
            <Button
              variant="ghost"
              size="sm"
              className="h-6 text-xs text-gray-500 dark:text-gray-400 px-1"
              onClick={() =>
                onChangeDraft(idx, {
                  measures: [
                    ...draft.measures,
                    {
                      field: draft.availableFields[0] || '',
                      aggregation: 'last',
                      alias: draft.availableFields[0] || '',
                    },
                  ],
                })
              }
            >
              <Plus className="h-3 w-3 mr-0.5" /> Add measure
            </Button>
          </div>
        </div>
      ))}

      {/* Schema picker */}
      {sourcesLoading ? (
        <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500 py-2">
          <Loader2 className="h-3.5 w-3.5 animate-spin" /> Loading schemas…
        </div>
      ) : (
        <div className="space-y-1">
          <Label className="text-[11px] text-gray-500 dark:text-gray-400">Add a schema</Label>
          <div className="grid grid-cols-1 gap-1 max-h-48 overflow-y-auto">
            {availableSources
              .filter((s) => !draftSources.some((d) => d.schemaId === s.id))
              .map((s) => (
                <button
                  key={s.id}
                  className="flex items-center justify-between text-left px-2 py-1.5 rounded border border-gray-200 dark:border-gray-700 hover:border-emerald-300 dark:hover:border-emerald-700 hover:bg-emerald-50 dark:hover:bg-emerald-900/30 transition-colors text-xs"
                  onClick={() => onAdd(s)}
                >
                  <span className="font-medium text-gray-700 dark:text-gray-300">{s.name}</span>
                  <Plus className="h-3.5 w-3.5 text-gray-400 dark:text-gray-500" />
                </button>
              ))}
            {availableSources.filter((s) => !draftSources.some((d) => d.schemaId === s.id)).length === 0 && (
              <p className="text-xs text-gray-400 dark:text-gray-500 py-1">All available schemas added.</p>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

function SaveTemplateFields({
  idPrefix = 'composite-save',
  name,
  description,
  tags,
  folder,
  onNameChange,
  onDescriptionChange,
  onTagsChange,
  onFolderChange,
}: {
  idPrefix?: string;
  name: string;
  description: string;
  tags: string;
  folder: string;
  onNameChange: (value: string) => void;
  onDescriptionChange: (value: string) => void;
  onTagsChange: (value: string) => void;
  onFolderChange: (value: string) => void;
}) {
  const handleFieldChange = (field: TemplateSaveMetadataField, value: string) => {
    if (field === 'name') {
      onNameChange(value);
      return;
    }

    if (field === 'description') {
      onDescriptionChange(value);
      return;
    }

    if (field === 'tags') {
      onTagsChange(value);
      return;
    }

    onFolderChange(value);
  };

  return (
    <div className="border rounded-lg p-3 bg-gray-50 dark:bg-gray-800/50 space-y-2">
      <p className="text-xs font-medium text-gray-700 dark:text-gray-300">Save as Template</p>
      <TemplateSaveMetadataFields
        idPrefix={idPrefix}
        value={{ name, description, tags, folder }}
        onFieldChange={handleFieldChange}
        gridClassName="grid gap-2 sm:grid-cols-2"
        inputClassName={compactSaveInputClassName}
        textareaClassName={compactSaveTextareaClassName}
      />
      <p className="text-[11px] text-gray-400 dark:text-gray-500">Saved multi-source templates will appear in the Templates tab.</p>
    </div>
  );
}

function VisualStep1({
  draftSources,
  primarySourceId,
  joinType,
  joinKeys,
  joinValidationMessage,
  onSetPrimary,
  onSetJoinType,
  onSetJoinKeys,
}: {
  draftSources: DraftSource[];
  primarySourceId: string;
  joinType: 'inner' | 'left' | 'outer';
  joinKeys: DraftJoinKey[];
  joinValidationMessage?: string | null;
  onSetPrimary: (id: string) => void;
  onSetJoinType: (t: 'inner' | 'left' | 'outer') => void;
  onSetJoinKeys: React.Dispatch<React.SetStateAction<DraftJoinKey[]>>;
}) {
  const allFields = Array.from(new Set(draftSources.flatMap((s) => s.availableFields)));

  const addKey = () =>
    onSetJoinKeys((prev) => [
      ...prev,
      {
        left: allFields[0] || 'time',
        right: allFields[0] || 'time',
        kind: 'nearest-timestamp',
        granularity: 'hour',
        toleranceMs: 300000,
      },
    ]);

  const removeKey = (i: number) => onSetJoinKeys((prev) => prev.filter((_, j) => j !== i));

  const patchKey = (i: number, patch: Partial<DraftJoinKey>) => onSetJoinKeys((prev) => prev.map((k, j) => (j === i ? { ...k, ...patch } : k)));

  return (
    <div className="space-y-4">
      {/* Primary source */}
      <div className="space-y-1.5">
        <Label className="text-xs font-medium">Primary source</Label>
        <p className="text-[11px] text-gray-400 dark:text-gray-500">Drives the left side of every join.</p>
        <div className="flex flex-wrap gap-2">
          {draftSources.map((s) => (
            <button
              key={s.sourceId}
              onClick={() => onSetPrimary(s.sourceId)}
              className={`px-2.5 py-1 rounded-full text-xs border transition-colors ${
                primarySourceId === s.sourceId
                  ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 font-medium'
                  : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {s.label}
            </button>
          ))}
        </div>
      </div>

      {/* Join type */}
      <div className="space-y-1.5">
        <Label className="text-xs font-medium">Row survival</Label>
        <div className="flex gap-2">
          {(['inner', 'left', 'outer'] as const).map((t) => (
            <button
              key={t}
              onClick={() => onSetJoinType(t)}
              className={`px-3 py-1 rounded text-xs border capitalize transition-colors ${
                joinType === t
                  ? 'border-emerald-500 bg-emerald-50 dark:bg-emerald-900/20 text-emerald-700 dark:text-emerald-400 font-medium'
                  : 'border-gray-200 dark:border-gray-700 text-gray-600 dark:text-gray-400 hover:border-gray-300 dark:hover:border-gray-600'
              }`}
            >
              {t}
            </button>
          ))}
        </div>
        <p className="text-[11px] text-gray-400 dark:text-gray-500">
          {joinType === 'inner' && 'Only rows matched in all sources.'}
          {joinType === 'left' && 'All primary rows; unmatched secondary values are null.'}
          {joinType === 'outer' && 'All rows from all sources; unmatched values are null.'}
        </p>
      </div>

      {/* Join keys */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-medium">Join keys</Label>
          <Button variant="ghost" size="sm" className="h-6 text-xs px-1.5" onClick={addKey}>
            <Plus className="h-3 w-3 mr-0.5" /> Add key
          </Button>
        </div>
        {joinValidationMessage && (
          <p className="flex items-start gap-1.5 rounded-md border border-amber-200 bg-amber-50 px-2.5 py-2 text-xs text-amber-800 dark:border-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
            <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>{joinValidationMessage}</span>
          </p>
        )}
        {joinKeys.map((key, i) => (
          <div key={i} className="border rounded-lg p-2.5 space-y-2 bg-gray-50 dark:bg-gray-800/50">
            <div className="flex gap-1 items-center">
              <div className="flex-1 space-y-0.5">
                <Label className="text-[10px] text-gray-400 dark:text-gray-500">Left field</Label>
                <Select value={key.left} onValueChange={(v) => patchKey(i, { left: v })}>
                  <SelectTrigger className="h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {allFields.map((f) => (
                      <SelectItem key={f} value={f} className="text-xs">
                        {f}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex-1 space-y-0.5">
                <Label className="text-[10px] text-gray-400 dark:text-gray-500">Right field</Label>
                <Select value={key.right} onValueChange={(v) => patchKey(i, { right: v })}>
                  <SelectTrigger className="h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {allFields.map((f) => (
                      <SelectItem key={f} value={f} className="text-xs">
                        {f}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <Button
                type="button"
                variant="ghost"
                size="sm"
                aria-label={`Remove join key ${i + 1}`}
                className="h-7 w-7 p-0 mt-4 shrink-0"
                onClick={() => removeKey(i)}
              >
                <Trash2 className="h-3 w-3 text-gray-400 dark:text-gray-500" />
              </Button>
            </div>
            <div className="flex gap-1">
              <div className="flex-1 space-y-0.5">
                <Label className="text-[10px] text-gray-400 dark:text-gray-500">Match strategy</Label>
                <Select
                  value={key.kind}
                  onValueChange={(value) => {
                    const kind = parseDraftJoinKeyKind(value);
                    if (kind) {
                      patchKey(i, { kind });
                    }
                  }}
                >
                  <SelectTrigger className="h-7 text-xs">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="exact" className="text-xs">
                      exact
                    </SelectItem>
                    <SelectItem value="time-bucket" className="text-xs">
                      time-bucket
                    </SelectItem>
                    <SelectItem value="nearest-timestamp" className="text-xs">
                      nearest-timestamp
                    </SelectItem>
                  </SelectContent>
                </Select>
              </div>
              {key.kind === 'nearest-timestamp' && (
                <div className="flex-1 space-y-0.5">
                  <Label className="text-[10px] text-gray-400 dark:text-gray-500">Tolerance (ms)</Label>
                  <Input type="number" className="h-7 text-xs" value={key.toleranceMs} onChange={(e) => patchKey(i, { toleranceMs: Number(e.target.value) })} />
                </div>
              )}
              {key.kind === 'time-bucket' && (
                <div className="flex-1 space-y-0.5">
                  <Label className="text-[10px] text-gray-400 dark:text-gray-500">Granularity</Label>
                  <Select
                    value={key.granularity}
                    onValueChange={(value) => {
                      const granularity = parseTimeGranularity(value);
                      if (granularity) {
                        patchKey(i, { granularity });
                      }
                    }}
                  >
                    <SelectTrigger className="h-7 text-xs">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIME_GRANULARITY_OPTIONS.map((g) => (
                        <SelectItem key={g} value={g} className="text-xs">
                          {g}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}

function VisualStep2({
  draftSources,
  fieldMappings,
  orderByField,
  orderByDirection,
  limit,
  saveName,
  saveDescription,
  saveTags,
  saveFolder,
  previewLoading,
  previewError,
  previewRows,
  onSetMappings,
  onSetOrderBy,
  onSetOrderByDirection,
  onSetLimit,
  onSaveNameChange,
  onSaveDescriptionChange,
  onSaveTagsChange,
  onSaveFolderChange,
  onAutoPopulate,
  onPreview,
}: {
  draftSources: DraftSource[];
  fieldMappings: CompositeFieldMapping[];
  orderByField: string;
  orderByDirection: 'asc' | 'desc';
  limit: number;
  saveName: string;
  saveDescription: string;
  saveTags: string;
  saveFolder: string;
  previewLoading: boolean;
  previewError: string | null;
  previewRows: Record<string, unknown>[] | null;
  onSetMappings: (m: CompositeFieldMapping[]) => void;
  onSetOrderBy: (f: string) => void;
  onSetOrderByDirection: (direction: 'asc' | 'desc') => void;
  onSetLimit: (n: number) => void;
  onSaveNameChange: (value: string) => void;
  onSaveDescriptionChange: (value: string) => void;
  onSaveTagsChange: (value: string) => void;
  onSaveFolderChange: (value: string) => void;
  onAutoPopulate: () => void;
  onPreview: () => void;
}) {
  const sourceFieldOptions = draftSources.flatMap((s) =>
    [s.timeField, ...s.measures.map((m) => m.alias || m.field)].map((f) => ({
      sourceId: s.sourceId,
      field: f,
    })),
  );

  const addMapping = () => onSetMappings([...fieldMappings, { sourceId: draftSources[0]?.sourceId || '', field: '', alias: '' }]);

  const removeMapping = (i: number) => onSetMappings(fieldMappings.filter((_, j) => j !== i));

  const patchMapping = (i: number, patch: Partial<CompositeFieldMapping>) => onSetMappings(fieldMappings.map((m, j) => (j === i ? { ...m, ...patch } : m)));

  return (
    <div className="space-y-4">
      {/* Field mappings */}
      <div className="space-y-2">
        <div className="flex items-center justify-between">
          <Label className="text-xs font-medium">Output fields</Label>
          <div className="flex gap-1">
            <Button variant="ghost" size="sm" className="h-6 text-xs px-1.5" onClick={onAutoPopulate}>
              Auto-populate
            </Button>
            <Button variant="ghost" size="sm" className="h-6 text-xs px-1.5" onClick={addMapping}>
              <Plus className="h-3 w-3 mr-0.5" /> Add
            </Button>
          </div>
        </div>
        <p className="text-[11px] text-gray-400 dark:text-gray-500">Each row maps a source field to an output alias used by the chart.</p>
        {fieldMappings.length === 0 && (
          <p className="text-xs text-gray-400 dark:text-gray-500 py-2 text-center">
            Click <em>Auto-populate</em> or add rows manually.
          </p>
        )}
        {fieldMappings.map((m, i) => (
          <div key={i} className="flex gap-1 items-center">
            <Select
              value={`${m.sourceId}:${m.field}`}
              onValueChange={(v) => {
                const [sid, ...rest] = v.split(':');
                const field = rest.join(':');
                patchMapping(i, {
                  sourceId: sid,
                  field,
                  alias: m.alias || field,
                });
              }}
            >
              <SelectTrigger className="h-7 text-xs flex-1">
                <SelectValue placeholder="source → field" />
              </SelectTrigger>
              <SelectContent>
                {sourceFieldOptions.map((opt) => (
                  <SelectItem key={`${opt.sourceId}:${opt.field}`} value={`${opt.sourceId}:${opt.field}`} className="text-xs">
                    {opt.sourceId} → {opt.field}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="text-gray-400 dark:text-gray-500 text-xs shrink-0">as</span>
            <Input className="h-7 text-xs flex-1" value={m.alias} placeholder="alias" onChange={(e) => patchMapping(i, { alias: e.target.value })} />
            <Button variant="ghost" size="sm" className="h-7 w-7 p-0 shrink-0" onClick={() => removeMapping(i)}>
              <Trash2 className="h-3 w-3 text-gray-400 dark:text-gray-500" />
            </Button>
          </div>
        ))}
      </div>

      {/* Options */}
      <div className="flex gap-3">
        <div className="flex-1 space-y-1">
          <Label className="text-[11px] text-gray-500 dark:text-gray-400">Order by alias</Label>
          <Input className="h-7 text-xs" value={orderByField} placeholder="e.g. time" onChange={(e) => onSetOrderBy(e.target.value)} />
        </div>
        <div className="w-24 space-y-1">
          <Label className="text-[11px] text-gray-500 dark:text-gray-400">Direction</Label>
          <Select value={orderByDirection} onValueChange={(value) => onSetOrderByDirection(value === 'desc' ? 'desc' : 'asc')}>
            <SelectTrigger className="h-7 text-xs">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="asc" className="text-xs">
                asc
              </SelectItem>
              <SelectItem value="desc" className="text-xs">
                desc
              </SelectItem>
            </SelectContent>
          </Select>
        </div>
        <div className="w-24 space-y-1">
          <Label className="text-[11px] text-gray-500 dark:text-gray-400">Limit</Label>
          <Input type="number" className="h-7 text-xs" value={limit} onChange={(e) => onSetLimit(Number(e.target.value))} />
        </div>
      </div>

      {/* Preview */}
      <Button
        variant="outline"
        size="sm"
        className="flex items-center gap-1.5 text-xs w-full"
        disabled={previewLoading || fieldMappings.length === 0}
        onClick={onPreview}
      >
        {previewLoading ? <Loader2 className="h-3 w-3 animate-spin" /> : <Eye className="h-3.5 w-3.5" />}
        Preview (first 5 rows)
      </Button>

      {previewError && <p className="text-xs text-red-600 bg-red-50 dark:bg-red-900/20 rounded px-2 py-1">{previewError}</p>}
      {previewRows && previewRows.length > 0 && <PreviewTable rows={previewRows} />}
      {previewRows && previewRows.length === 0 && !previewError && (
        <p className="text-xs text-amber-700 dark:text-amber-400 bg-amber-50 dark:bg-amber-900/20 rounded px-2 py-1">
          No data returned for the selected time range and join configuration.
        </p>
      )}

      {fieldMappings.length > 0 && (
        <SaveTemplateFields
          idPrefix="visual-composite-save"
          name={saveName}
          description={saveDescription}
          tags={saveTags}
          folder={saveFolder}
          onNameChange={onSaveNameChange}
          onDescriptionChange={onSaveDescriptionChange}
          onTagsChange={onSaveTagsChange}
          onFolderChange={onSaveFolderChange}
        />
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Mini preview table
// ---------------------------------------------------------------------------

function PreviewTable({ rows }: { rows: Record<string, unknown>[] }) {
  const columns = Object.keys(rows[0] || {}).slice(0, 6);
  return (
    <div className="overflow-x-auto rounded border border-gray-200 dark:border-gray-700">
      <table className="w-full text-[10px]">
        <thead className="bg-gray-50 dark:bg-gray-800/50">
          <tr>
            {columns.map((c) => (
              <th key={c} className="text-left px-2 py-1 font-medium text-gray-600 dark:text-gray-400 border-b border-gray-200 dark:border-gray-700">
                {c}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, i) => (
            <tr key={i} className="border-b border-gray-100 dark:border-gray-700 last:border-0">
              {columns.map((c) => (
                <td key={c} className="px-2 py-1 text-gray-700 dark:text-gray-300 max-w-[100px] truncate">
                  {String(row[c] ?? '')}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
